/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SvgIcon from '@mui/material/SvgIcon';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useSupplierSearch } from '../../hooks/useSupplierSearch';
import { useFactoryData } from '../../hooks/useFactoryData';
import useAuthProviderStore from '../../stores/auth_provider.store';
import permissionManager from '../../lib/permission.manager';
import ProfileTab from './ProfileTab';
import PreferencesTab from './PreferencesTab';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

function UserProfile() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  const preferences = useUserPreferences();
  const supplierSearch = useSupplierSearch();
  const factoryData = useFactoryData();
  const { userPermissions } = useAuthProviderStore();

  const isExternalUser = React.useMemo(
    () => permissionManager.isUserSupplier(userPermissions),
    [userPermissions],
  );

  useEffect(() => {
    preferences.fetchPreferences().then(() => {
      setHasInitiallyLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isExternalUser &&
      supplierSearch.autoLoadedSupplier &&
      !preferences.selectedSupplier
    ) {
      preferences.setSelectedSupplier(supplierSearch.autoLoadedSupplier);
      supplierSearch.setSupplierSearch(
        supplierSearch.autoLoadedSupplier.business_name,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExternalUser, supplierSearch.autoLoadedSupplier]);

  useEffect(() => {
    if (preferences.selectedSupplier) {
      supplierSearch.setSupplierSearch(
        preferences.selectedSupplier.business_name,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences.selectedSupplier]);

  useEffect(() => {
    if (tabValue !== 1) {
      preferences.resetToSaved();
      supplierSearch.setSupplierSearch(
        preferences.savedSupplier?.business_name || '',
      );
      supplierSearch.setShowResults(false);
    } else {
      if (preferences.selectedSupplier) {
        supplierSearch.setSupplierSearch(
          preferences.selectedSupplier.business_name,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const selectedFactoryKey = React.useMemo(() => {
    return preferences.selectedFactories
      .map((f) => f.dss_id || f.factory_id || f.id)
      .sort()
      .join('|');
  }, [preferences.selectedFactories]);

  useEffect(() => {
    if (!selectedFactoryKey) {
      preferences.setSelectedCountries([]);
      factoryData.setAvailableCountries([]);

      if (preferences.selectedSupplier?.departments) {
        factoryData.setAvailableDepartments(
          preferences.selectedSupplier.departments,
        );

        const supplierDeptIds = new Set(
          preferences.selectedSupplier.departments.map((d) => d.id),
        );
        const filteredDepartments = preferences.selectedDepartments.filter(
          (dept) => supplierDeptIds.has(dept.id),
        );

        if (
          filteredDepartments.length !== preferences.selectedDepartments.length
        ) {
          preferences.setSelectedDepartments(filteredDepartments);
        }
      } else {
        factoryData.setAvailableDepartments([]);
        preferences.setSelectedDepartments([]);
      }
      return;
    }

    factoryData.fetchDepartmentsFromFactories(preferences.selectedFactories);
  }, [selectedFactoryKey, preferences.selectedSupplier]);

  useEffect(() => {
    if (!hasInitiallyLoaded) {
      return;
    }

    if (preferences.selectedFactories.length === 0) {
      return;
    }

    if (factoryData.isLoadingDepartments) {
      return;
    }

    const availableIds = new Set(
      factoryData.availableDepartments.map((dept) => dept.id),
    );
    const filteredSelected = preferences.selectedDepartments.filter((dept) =>
      availableIds.has(dept.id),
    );

    if (filteredSelected.length !== preferences.selectedDepartments.length) {
      preferences.setSelectedDepartments(filteredSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInitiallyLoaded,
    factoryData.availableDepartments,
    factoryData.isLoadingDepartments,
    preferences.selectedFactories.length,
  ]);

  useEffect(() => {
    if (preferences.selectedFactories.length === 0) {
      return;
    }

    if (factoryData.isLoadingDepartments) {
      return;
    }

    if (factoryData.availableCountries.length === 0) {
      return;
    }

    const availableCountryIds = new Set(
      factoryData.availableCountries.map((country) => country.id),
    );
    const filteredCountries = preferences.selectedCountries.filter((country) =>
      availableCountryIds.has(country.id),
    );

    if (filteredCountries.length !== preferences.selectedCountries.length) {
      preferences.setSelectedCountries(filteredCountries);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    factoryData.availableCountries,
    factoryData.isLoadingDepartments,
    preferences.selectedFactories.length,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (supplierSearch.supplierSearch && !preferences.selectedSupplier) {
        supplierSearch.searchSuppliers(supplierSearch.supplierSearch);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierSearch.supplierSearch, preferences.selectedSupplier]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.supplier-search-container')) {
        supplierSearch.setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveChanges = useCallback(async () => {
    if (tabValue !== 1) {
      return;
    }

    setIsSaving(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      const result = await preferences.savePreferences();

      if (result.success) {
        setSaveSuccess(true);
      } else {
        setSaveError('Failed to save preferences. Please try again.');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setSaveError('Failed to save preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [tabValue, preferences]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleBack = useCallback(() => {
    navigate('/apps');
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setSaveSuccess(false);
    setSaveError('');
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Snackbar
        open={saveSuccess || !!saveError}
        autoHideDuration={saveSuccess ? 3000 : 5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={saveSuccess ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {saveSuccess ? '✓ Preferences saved successfully!' : saveError}
        </Alert>
      </Snackbar>

      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <SvgIcon>
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </SvgIcon>
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              <SvgIcon
                sx={{ fontSize: '1.5rem', verticalAlign: 'middle', mr: 1 }}
              >
                <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
              </SvgIcon>
              User Profile Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your account and preferences
            </Typography>
          </Box>
        </Box>

        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              bgcolor: '#f5f5f5',
              px: 2,
              '& .MuiTab-root': {
                minHeight: 64,
              },
            }}
          >
            <Tab
              icon={
                <SvgIcon>
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </SvgIcon>
              }
              iconPosition="start"
              label="Profile"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={
                <SvgIcon>
                  <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
                </SvgIcon>
              }
              iconPosition="start"
              label="Prefs"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ px: 4 }}>
              <ProfileTab />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ px: 4 }}>
              <PreferencesTab
                isLoadingPreferences={preferences.isLoadingPreferences}
                selectedSupplier={preferences.selectedSupplier}
                setSelectedSupplier={preferences.setSelectedSupplier}
                selectedFactories={preferences.selectedFactories}
                setSelectedFactories={preferences.setSelectedFactories}
                selectedDepartments={preferences.selectedDepartments}
                setSelectedDepartments={preferences.setSelectedDepartments}
                selectedCountries={preferences.selectedCountries}
                setSelectedCountries={preferences.setSelectedCountries}
                supplierSearch={supplierSearch.supplierSearch}
                setSupplierSearch={supplierSearch.setSupplierSearch}
                supplierResults={supplierSearch.supplierResults}
                isSearching={supplierSearch.isSearching}
                searchError={supplierSearch.searchError}
                showResults={supplierSearch.showResults}
                setShowResults={supplierSearch.setShowResults}
                availableDepartments={factoryData.availableDepartments}
                setAvailableDepartments={factoryData.setAvailableDepartments}
                availableCountries={factoryData.availableCountries}
                isLoadingDepartments={factoryData.isLoadingDepartments}
                isExternalUser={isExternalUser}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  size="large"
                  startIcon={
                    isSaving ? null : (
                      <SvgIcon>
                        <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" />
                      </SvgIcon>
                    )
                  }
                  sx={{ textTransform: 'none', px: 4 }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </TabPanel>
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          py: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          copyright © {new Date().getFullYear()} Kmart. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}

export default UserProfile;
